import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthPermission } from 'skipify-types';

import { useMerchant } from '../contexts/MerchantContext';
import ApplicationLoading from '../common/ApplicationLoading';
import { UnauthorizedMerchant } from './UnauthorizedMerchant';
import { buildUrlWithMerchantId } from '../util/helpers';
import { configurationPaths, tabs } from '../util/constants';

type Props = {
  children?: React.ReactNode;
  fallbackPath?: string;
  isFallbackRoute?: boolean;
  routePermissions?: readonly AuthPermission[];
};

export const AuthenticatedRouteGuard: React.FC<Props> = ({
  children,
  fallbackPath,
  routePermissions,
  isFallbackRoute = false,
}) => {
  const location = useLocation();
  const {
    permissions,
    isLoading,
    getHomePath,
    merchantId,
    canAccess,
    unauthorizedMerchantId,
    requiresParentSelection,
    isChildMerchantRestrictedShell,
  } = useMerchant();

  // Show loading screen while merchant data is being fetched
  if (isLoading || (!merchantId && !requiresParentSelection)) {
    return <ApplicationLoading />;
  }

  if (unauthorizedMerchantId || !permissions) {
    return <UnauthorizedMerchant />;
  }

  const paylinksSectionBase = `/${tabs.PAYLINKS}`;
  const isPaylinksAppSection =
    location.pathname === paylinksSectionBase ||
    location.pathname.startsWith(`${paylinksSectionBase}/`);
  if (isChildMerchantRestrictedShell && isPaylinksAppSection) {
    return (
      <Navigate to={buildUrlWithMerchantId(configurationPaths.SETTINGS, merchantId)} replace />
    );
  }

  const userHasRoutePermissions = !routePermissions || routePermissions.some((p) => canAccess(p));

  // If permissions are empty array (user confirmed to have no permissions), or user does not have route permissions, redirect
  if (permissions.length === 0 || !userHasRoutePermissions || isFallbackRoute) {
    const homePath = fallbackPath ?? getHomePath();
    return <Navigate to={buildUrlWithMerchantId(homePath, merchantId)} replace />;
  }

  // If no children provided (using as a layout, for example), render the outlet
  if (!children) {
    return <Outlet />;
  }

  return children as React.ReactElement;
};
